import { toast } from 'react-toastify';
import { matchPath } from 'react-router-dom';
import { adminLogout, logout } from './actions/login';
import { clear, storedImpersonationScope } from './utils/tokenUtils';
import { USER_TYPE_ADMIN, USER_TYPE_REP, USER_TYPE_RETAILER } from './data/userTypes';

class AuthorizationHandler {
  static instance;

  ERROR_MESSAGE = 'You do not have access to {area} area. Please log in to view.';

  AUTHORIZED_ROUTES = {
    [USER_TYPE_RETAILER]: '/',
    [USER_TYPE_REP]: '/team/login',
    [USER_TYPE_ADMIN]: '/admin/login',
  };

  LOGOUT_ACTIONS = {
    [USER_TYPE_RETAILER]: logout,
    [USER_TYPE_REP]: logout,
    [USER_TYPE_ADMIN]: adminLogout,
  };

  IMPERSONATION_SCOPE_AREAS = {
    admin: USER_TYPE_ADMIN,
    team: USER_TYPE_REP,
  };

  constructor(path) {
    this.path = path;
  }

  impersonatorArea() {
    const scope = storedImpersonationScope();

    return scope ? this.IMPERSONATION_SCOPE_AREAS[scope] || null : null;
  }

  shouldResolve() {
    if (this.impersonatorArea()) {
      return true;
    }

    return !this.skipsAuthorize();
  }

  skipsAuthorize() {
    const authNotRequiredRoutes = [
      '/contact',
      '/reset-password',
      '/password-reset-link/:id',
      '/enter-pin',
      '/seq-questions',
      '/new-password',
      '/team/reset-password',
      '/team/enter-pin',
      '/team/seq-questions',
      '/team/new-password',
      '/registration/outlet',
      '/registration/about-you',
      '/users/about_you',
      '/registration/your-store',
      '/registration/store-summary',
      '/admin/welcome',
      '/team/welcome',
      '/content-pages/terms-conditions',
      '/content-pages/privacy-policy',
      '/privacy-policy.html'
    ];

    authNotRequiredRoutes.push('/');
    authNotRequiredRoutes.push('/about-ignite');

    const exemptPaths = authNotRequiredRoutes.concat(Object.values(this.AUTHORIZED_ROUTES));

    return exemptPaths.find(path => matchPath({ path, end: true }, this.path));
  }

  mapPathToUserArea() {
    const impersonatorArea = this.impersonatorArea();
    if (impersonatorArea) {
      return impersonatorArea;
    }

    const {path} = this;
    if (typeof path !== 'string') {
      throw Error('Error in auth handler: path must be a string');
    }
    if (path === '/') {
      return USER_TYPE_RETAILER;
    }
    const pathArea = path.substring(1).split('/')
      .shift();

    if (pathArea === 'team') {
      return USER_TYPE_REP;
    } else if (pathArea === 'admin') {
      return USER_TYPE_ADMIN;
    }

    return USER_TYPE_RETAILER;
  }

  resolveError() {
    const area = this.mapPathToUserArea();
    const impersonating = Boolean(this.impersonatorArea());
    return (dispatch, history) => {
      const redirectTo = this.AUTHORIZED_ROUTES[area];

      if (impersonating) {
        clear();
        dispatch({ type: 'CLEAR_PROFILE' });
        dispatch({ type: 'CLEAR_REQUESTED_PATH' });
        toast.error(this.ERROR_MESSAGE.replace('{area}', area));
        history.push(redirectTo);
        return;
      }

      const doLogout = this.LOGOUT_ACTIONS[area];
      dispatch(doLogout()).then(() => {
        toast.error(this.ERROR_MESSAGE.replace('{area}', area));
        history.push(redirectTo);
      });
    };
  }
}

export default AuthorizationHandler;
